/**
 * The rules behind the player's Auctions list (#236, PL-019.1: Player/AuctionPlayer.js), kept pure
 * so they can be tested on their own.
 *
 * One row per auction (Player/GetAuctions, usp_GetAllPlayerAuction). The page used to list lots
 * (Bid/GetBidWithStatus), so an auction could be cut across two pages and be on both tabs. The
 * server decides everything a row shows: the auction's status (tblAuctions.Status), its pool, the
 * lot bidding is open on (liveTeamId) and what the player may do (canBid, canPurchaseAuctionPass,
 * canRedeemAuctionPassCredit). These functions only map that to the screen; the rules for Place Bid
 * and the pass are the ones the lot grid had, lifted to the auction.
 */

import dayjs from 'dayjs';
import { deriveAuctionStatus } from './auctionGrid';

/** The two tabs, as the server's Tab parameter spells them. */
export const AUCTION_TABS = { LIVE: 'Live', CLOSED: 'Closed' };

/** The tabs' labels. */
export const AUCTION_TAB_LABELS = { Live: 'Live and Upcoming', Closed: 'Closed' };

/** Auctions per page on both tabs. */
export const AUCTIONS_PAGE_SIZE = 10;

/**
 * The tab a URL asks for: "?tab=closed" is the Closed tab, anything else Live and Upcoming, which is
 * where the sidebar and Auction Details' "Auctions" back link open the page.
 *
 * @param {string|null} value The tab query parameter.
 * @returns {'Live'|'Closed'}
 */
export const tabFromQuery = (value) =>
    String(value ?? '').toLowerCase() === 'closed' ? AUCTION_TABS.CLOSED : AUCTION_TABS.LIVE;

const BADGES = { Live: 'live', Paused: 'live', Upcoming: 'upcoming', Closed: 'closed', Archive: 'closed' };

/** The Bid Status badge's text for each badge. */
export const BADGE_LABELS = { live: 'Live', upcoming: 'Upcoming', closed: 'Closed' };

/**
 * The Bid Status badge for an auction's status. A paused auction is still running, so it shows
 * Live (with Place Bid greyed out); Archive is an auction that closed with no bids.
 *
 * @param {string} status tblAuctions.Status: Upcoming, Live, Paused, Closed or Archive.
 * @returns {'live'|'upcoming'|'closed'}
 */
export const badgeFor = (status) => BADGES[status] ?? 'upcoming';

/** What the Action column offers. */
export const AUCTION_ACTIONS = {
    RESULTS: 'results',
    BID: 'bid',
    UNLOCK: 'unlock',
    CREDIT: 'credit',
    BID_DISABLED: 'bid-disabled',
};

/**
 * The Action column's labels. "Unlock $4.99" and "Use credit" keep the lot grid's wording; the
 * price the player pays is the server's, shown in the unlock dialog.
 */
export const ACTION_LABELS = {
    results: 'View Results',
    bid: 'Place Bid',
    unlock: 'Unlock $4.99',
    credit: 'Use credit',
    'bid-disabled': 'Place Bid',
};

/**
 * The action a row offers, in the lot grid's order (Place Bid, then the pass, then the credit),
 * lifted to the auction:
 * - a closed auction: View Results;
 * - a live auction with a lot open for bids, to a player who may bid: Place Bid;
 * - otherwise the single-auction pass while the server sells it, or a credit: until bidding on the
 *   auction closes, upcoming or live (#289);
 * - otherwise Place Bid greyed out: an unlocked auction that has not started, a paused one, or a
 *   running one the player has not paid for and is offered nothing to unlock it with.
 *
 * @param {object} row A list row.
 * @returns {string} One of AUCTION_ACTIONS.
 */
export const actionFor = (row) => {
    if (badgeFor(row?.status) === 'closed') return AUCTION_ACTIONS.RESULTS;
    if (row?.status === 'Live' && row?.liveTeamId && row?.canBid) return AUCTION_ACTIONS.BID;
    if (row?.canPurchaseAuctionPass) return AUCTION_ACTIONS.UNLOCK;
    if (row?.canRedeemAuctionPassCredit) return AUCTION_ACTIONS.CREDIT;
    return AUCTION_ACTIONS.BID_DISABLED;
};

/**
 * The Auction Details page of an auction (#237). The auction is in the URL, so a reload, Back and
 * a return from the payment page all reopen it.
 *
 * @param {number|string} auctionId The auction.
 * @returns {string}
 */
export const detailsPath = (auctionId) => `/Player/AuctionDetails/${auctionId}`;

/**
 * Whether a path is an Auction Details page, where the sidebar's Auction item stays active.
 *
 * @param {*} path A pathname.
 * @returns {boolean}
 */
export const isAuctionDetailsPath = (path) => typeof path === 'string' && path.startsWith('/Player/AuctionDetails/');

/**
 * Where a click on a row goes (#237 AC1): a Live or Upcoming auction opens Auction Details; a
 * closed row is not clickable (only its View Results is).
 *
 * @param {object} row A list row.
 * @returns {{path: string}|null}
 */
export const rowTarget = (row) =>
    row?.auctionId == null || badgeFor(row?.status) === 'closed' ? null : { path: detailsPath(row.auctionId) };

/**
 * Where a row's Place Bid and View Results go.
 *
 * - Closed: the Auction Results page, which opens on that auction ({auctionId, auctionName}).
 * - Otherwise (Place Bid): Auction Details, which selects the team being auctioned (#237).
 *
 * @param {object} row A list row.
 * @returns {{path: string, state?: object}|null}
 */
export const actionTarget = (row) => {
    if (badgeFor(row?.status) === 'closed') {
        return {
            path: '/Player/AuctionResults',
            state: { auctionId: row.auctionId, auctionName: row.auctionName },
        };
    }
    return rowTarget(row);
};

/**
 * The Manager filter's options: the managers the player is subscribed to (Approved), without
 * blocked ones, once each - an old database can hold two rows for one manager (#133). The practice
 * manager is included like any other.
 *
 * @param {Array} subscriptions Player/GetSubscribedManager's rows.
 * @returns {Array<{label: string, value: number}>}
 */
export const managerOptions = (subscriptions) =>
    (Array.isArray(subscriptions) ? subscriptions : [])
        .filter((manager) => manager?.status === 'Approved' && !manager?.isManagerBlocked)
        .filter((manager, index, all) => all.findIndex((other) => other.managerId === manager.managerId) === index)
        .map((manager) => ({ label: manager.managerName || '', value: manager.managerId }));

/**
 * The ManagerIds the server is sent: only a strict subset of the managers narrows the list. All of
 * them, or none (the filter emptied), is "All managers" and sends nothing.
 *
 * @param {Array<{value: number}>} selected The ticked options.
 * @param {Array<{value: number}>} options Every option.
 * @returns {string} e.g. "12,40", or "".
 */
export const managerIdsParam = (selected, options) => {
    const ids = (selected || []).map((option) => option.value);
    if (ids.length === 0 || ids.length >= (options || []).length) return '';
    return [...ids].sort((a, b) => a - b).join(',');
};

/**
 * The Manager filter's text: "All managers" for all of them or none, the name for one, a count for
 * more.
 *
 * @param {Array} selected The ticked options.
 * @param {Array} options Every option.
 * @returns {string}
 */
export const managerFilterLabel = (selected, options) => {
    const count = (selected || []).length;
    if (count === 0 || count >= (options || []).length) return 'All managers';
    if (count === 1) return selected[0].label;
    return `${count} managers`;
};

/**
 * The empty table's text. The hint is shown only when a search or the Manager filter narrowed the
 * list; with neither, the player simply has no auctions on that tab.
 *
 * @param {{narrowed: boolean}} options
 * @returns {{title: string, hint: string|null}}
 */
export const emptyStateText = ({ narrowed }) => ({
    title: 'No auctions found',
    hint: narrowed ? 'Try a different name, contest or sport, or choose “All managers”.' : null,
});

/**
 * The "Showing N auctions" line.
 *
 * @param {number} count The auctions matching the tab, search and filter.
 * @returns {string}
 */
export const showingText = (count) => `Showing ${count} ${count === 1 ? 'auction' : 'auctions'}`;

/**
 * An auction's start, set in the Manager portal, in the player's local time like every other date on
 * the web: MM/DD/YYYY hh:mm AM/PM. The server sends UTC without a zone; a value with one is read
 * as it is.
 *
 * @param {string} startDate The row's startDate.
 * @returns {string} e.g. "10/21/2026 12:30 PM", or "-" when there is no date.
 */
export const formatStart = (startDate) => {
    if (!startDate) return '-';
    const text = String(startDate);
    const parsed = dayjs(/(Z|[+-]\d{2}:?\d{2})$/i.test(text) ? text : `${text}Z`);
    return parsed.isValid() ? parsed.format('MM/DD/YYYY hh:mm A') : '-';
};

/**
 * The search a start reminder opens the list with (#196): the auction's name, trimmed.
 *
 * @param {string} auctionName The reminder's auction name.
 * @returns {string}
 */
export const reminderSearch = (auctionName) =>
    typeof auctionName === 'string' ? auctionName.trim().slice(0, 256).trim() : '';

const isClosedLot = (lot) => lot?.status === 'Closed' || lot?.status === 'Archive';

/**
 * What one auction's lots say about it: deriveAuctionStatus's answer (Live, Paused or Upcoming),
 * Closed when every lot is closed, or null when they cannot tell (one closed and the next not open
 * yet). For a push, and for Auction Details, which holds every lot (#237).
 *
 * @param {Array} lots The lots of one auction.
 * @returns {'Live'|'Paused'|'Upcoming'|'Closed'|null}
 */
export const pushedAuctionStatus = (lots) => deriveAuctionStatus(lots) ?? (lots.length > 0 && lots.every(isClosedLot) ? 'Closed' : null);

/**
 * Applies a lot push (ReceiveBidStatus, ReceiveCurrentBid) to the auctions on the page.
 *
 * The pushes carry lots, not auctions, and only the auctions the player may see (#180); an auction
 * missing from a push is unchanged. For a running auction they patch the pool, the lot bidding is
 * open on, and Live/Paused. An auction that starts (Upcoming to Live, AC13) or ends (to Closed)
 * changes tabs and the order of the rows, so it is not patched: the caller reloads the page instead.
 *
 * @param {Array} rows The rows on the page.
 * @param {Array} lots The pushed lots.
 * @returns {{rows: Array, needsReload: boolean}} The same rows array when nothing changed.
 */
export const applyPushedLots = (rows, lots) => {
    if (!Array.isArray(rows) || !Array.isArray(lots) || lots.length === 0) return { rows, needsReload: false };

    const lotsByAuction = new Map();
    lots.forEach((lot) => {
        if (!lot) return;
        lotsByAuction.set(lot.auctionId, [...(lotsByAuction.get(lot.auctionId) || []), lot]);
    });

    let needsReload = false;
    let changed = false;
    const next = rows.map((row) => {
        const auctionLots = lotsByAuction.get(row?.auctionId);
        if (!auctionLots) return row;

        const pushed = pushedAuctionStatus(auctionLots);
        const badge = badgeFor(row.status);
        if ((badge === 'upcoming' && pushed && badgeFor(pushed) === 'live') || (badge === 'live' && pushed === 'Closed')) {
            needsReload = true;
            return row;
        }

        const update = {};
        const pool = auctionLots.find((lot) => lot.totalPoolAmount !== undefined && lot.totalPoolAmount !== null);
        if (pool && pool.totalPoolAmount !== row.totalPoolAmount) update.totalPoolAmount = pool.totalPoolAmount;
        if (badge === 'live' && (pushed === 'Live' || pushed === 'Paused')) {
            if (pushed !== row.status) update.status = pushed;
            const running = auctionLots.find((lot) => lot.status === 'Live' || lot.status === 'Paused');
            if (running && running.teamId !== row.liveTeamId) update.liveTeamId = running.teamId;
        }
        if (Object.keys(update).length === 0) return row;

        changed = true;
        return { ...row, ...update };
    });

    return { rows: changed ? next : rows, needsReload };
};
